import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { ShoppingBag, Ticket } from 'lucide-react'
import {
  Button,
  Card,
  Drawer,
  EmptyState,
  ErrorState,
  Skeleton,
  StatusBadge,
  TextField,
  ProductCoverPlaceholder,
} from '@shared/design-system/ui'
import { affiliateInviteUrl } from '@shared/config/public-urls'
import { useMediaQuery } from '@shared/lib/use-media-query'
import { productCategoryLabel } from '@entities/product/api/product.contract'
import {
  affiliationStatusLabel,
  inviteTokenFromInput,
  percentageLabel,
  useMarketplaceProductsQuery,
  useMyAffiliationsQuery,
} from '@entities/partnership'
import type { AffiliationStatus, MarketplaceProduct } from '@entities/partnership'
import { toast } from '@shared/lib/toast/toast-store'
import { affiliationStatusTone } from '../model/affiliation-status'
import { useAffiliateByInviteMutation, useAffiliateMutation } from '../api/affiliate.mutations'
import { affiliationRequestErrorFeedback } from '../lib/affiliation-request-error-feedback'
import {
  cardBody,
  cardCategory,
  cardFooter,
  cardName,
  commission,
  cover,
  coverFallback,
  grid,
  inviteForm,
  loadMoreRow,
  panel,
  panelHeader,
  panelHint,
  productCard,
  productProgress,
  scrollSentinel,
} from './affiliates.css'

const MOBILE_PAGE_SIZE = 3
const TABLET_PAGE_SIZE = 6
const DESKTOP_PAGE_SIZE = 9

export function MarketplacePanel({ onInviteAccepted }: { onInviteAccepted?: () => void }) {
  const marketplace = useMarketplaceProductsQuery()
  const mine = useMyAffiliationsQuery()
  const affiliate = useAffiliateMutation()
  const byInvite = useAffiliateByInviteMutation()
  const [inviteOpen, setInviteOpen] = useState(false)
  const [inviteValue, setInviteValue] = useState('')
  const [loadedPages, setLoadedPages] = useState(1)
  const sentinelRef = useRef<HTMLDivElement | null>(null)
  const isTablet = useMediaQuery('(min-width: 640px)')
  const isDesktop = useMediaQuery('(min-width: 1024px)')
  const pageSize = isDesktop ? DESKTOP_PAGE_SIZE : isTablet ? TABLET_PAGE_SIZE : MOBILE_PAGE_SIZE

  const statusByProduct = new Map<string, AffiliationStatus>(
    (mine.data ?? []).map((item) => [item.productId, item.status]),
  )
  const visibleCount = Math.min(marketplace.data?.length ?? 0, loadedPages * pageSize)
  const visibleProducts = useMemo(
    () => marketplace.data?.slice(0, visibleCount) ?? [],
    [marketplace.data, visibleCount],
  )
  const hasMoreProducts = visibleCount < (marketplace.data?.length ?? 0)

  const loadMoreProducts = useCallback(() => {
    if (!hasMoreProducts) {
      return
    }
    setLoadedPages((current) => current + 1)
  }, [hasMoreProducts])

  useEffect(() => {
    const node = sentinelRef.current
    if (!node || !hasMoreProducts || typeof IntersectionObserver === 'undefined') {
      return undefined
    }

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          loadMoreProducts()
        }
      },
      { rootMargin: '360px 0px' },
    )
    observer.observe(node)

    return () => {
      observer.disconnect()
    }
  }, [hasMoreProducts, loadMoreProducts])

  function handleAffiliate(product: MarketplaceProduct) {
    affiliate.mutate(product.id, {
      onSuccess: () => toast.success(`Afiliação solicitada para ${product.name}.`),
      onError: (error) => toast.error(affiliationRequestErrorFeedback(error, 'marketplace')),
    })
  }

  function handleInviteSubmit() {
    const token = inviteTokenFromInput(inviteValue)
    if (!token) {
      toast.error({
        title: 'Convite necessário',
        description: 'Cole o link ou o código do convite.',
      })
      return
    }
    byInvite.mutate(token, {
      onSuccess: () => {
        toast.success('Afiliação por convite solicitada.')
        setInviteOpen(false)
        setInviteValue('')
        onInviteAccepted?.()
      },
      onError: (error) => toast.error(affiliationRequestErrorFeedback(error, 'invite')),
    })
  }

  function closeInviteDrawer() {
    setInviteOpen(false)
    setInviteValue('')
  }

  return (
    <div className={panel}>
      <div className={panelHeader}>
        <span className={panelHint}>Produtos abertos para afiliação no marketplace.</span>
        <Button
          variant="secondary"
          size="sm"
          leftIcon={<Ticket size={16} />}
          onClick={() => {
            setInviteOpen(true)
          }}
        >
          Tenho um convite
        </Button>
      </div>

      {marketplace.isPending ? (
        <div className={grid}>
          {[0, 1, 2].map((index) => (
            <Card key={index} padding="md" className={productCard}>
              <Skeleton width="100%" height={132} />
              <Skeleton width="70%" height={16} />
              <Skeleton width="40%" height={12} />
            </Card>
          ))}
        </div>
      ) : marketplace.isError ? (
        <ErrorState
          onRetry={() => {
            void marketplace.refetch()
          }}
        />
      ) : marketplace.data.length === 0 ? (
        <EmptyState
          icon={<ShoppingBag size={22} />}
          title="Nenhum produto no marketplace"
          description="Quando produtores abrirem produtos para afiliação, eles aparecem aqui."
        />
      ) : (
        <>
          <div className={grid}>
            {visibleProducts.map((product) => {
              const status = statusByProduct.get(product.id)
              const justRequested = affiliate.isSuccess && affiliate.variables === product.id
              const pending =
                (affiliate.isPending && affiliate.variables === product.id) || justRequested
              return (
                <Card key={product.id} padding="md" className={productCard}>
                  {product.coverImage ? (
                    <div
                      className={cover}
                      style={{ backgroundImage: `url(${product.coverImage})` }}
                      role="img"
                      aria-label={product.name}
                    />
                  ) : (
                    <div className={coverFallback}>
                      <ProductCoverPlaceholder name={product.name} />
                    </div>
                  )}
                  <div className={cardBody}>
                    <span className={cardName}>{product.name}</span>
                    <span className={cardCategory}>
                      {productCategoryLabel(product.category || product.productType)}
                    </span>
                  </div>
                  <div className={cardFooter}>
                    <span className={commission}>
                      {product.comissaoAfiliado
                        ? `${percentageLabel(product.comissaoAfiliado)} de comissão`
                        : 'Comissão a combinar'}
                    </span>
                    {status ? (
                      <StatusBadge
                        tone={affiliationStatusTone(status)}
                        label={affiliationStatusLabel(status)}
                        size="sm"
                      />
                    ) : (
                      <Button
                        size="sm"
                        disabled={pending}
                        onClick={() => {
                          handleAffiliate(product)
                        }}
                      >
                        {pending ? 'Afiliando...' : 'Afiliar-se'}
                      </Button>
                    )}
                  </div>
                </Card>
              )
            })}
          </div>
          <div ref={sentinelRef} className={scrollSentinel} aria-hidden="true" />
          {hasMoreProducts ? (
            <div className={loadMoreRow}>
              <span className={productProgress}>
                {visibleCount} de {marketplace.data.length} produtos
              </span>
              <Button variant="secondary" size="sm" onClick={loadMoreProducts}>
                Mostrar mais produtos
              </Button>
            </div>
          ) : null}
        </>
      )}

      <Drawer
        open={inviteOpen}
        onClose={closeInviteDrawer}
        title="Afiliar por convite"
        description="Cole o link ou código enviado pelo produtor."
        size="compact"
        variant="form"
        busy={byInvite.isPending}
        dirty={inviteValue.trim().length > 0}
        footer={
          <>
            <Button variant="secondary" disabled={byInvite.isPending} onClick={closeInviteDrawer}>
              Cancelar
            </Button>
            <Button disabled={byInvite.isPending} onClick={handleInviteSubmit}>
              {byInvite.isPending ? 'Afiliando...' : 'Afiliar'}
            </Button>
          </>
        }
      >
        <div className={inviteForm}>
          <TextField
            label="Link ou código do convite"
            placeholder={affiliateInviteUrl('codigo')}
            value={inviteValue}
            onChange={(event) => {
              setInviteValue(event.target.value)
            }}
          />
        </div>
      </Drawer>
    </div>
  )
}
